<template>
  <div class="dj-login">
    <!-- ================= HERO (ambient video) ================= -->
    <section class="dj-login__hero">
      <video class="dj-login__video" autoplay muted loop playsinline poster="">
        <source src="/videos/login-bg.mp4" type="video/mp4" />
      </video>
      <div class="dj-login__scrim"></div>

      <div class="dj-login__hero-content">
        <router-link to="/" class="dj-login__brand">
          <v-img
            :src="require('../../assets/logo.png')"
            max-width="46"
            contain
            class="dj-login__logo"
          ></v-img>
          <div>
            <div class="dj-login__brand-name">DJ</div>
            <div class="dj-login__brand-sub">Daily Jobs · Marine Suite</div>
          </div>
        </router-link>

        <div class="dj-login__hero-copy">
          <h1 class="dj-login__headline">
            Precision costing for<br />every vessel.
          </h1>
          <p class="dj-login__lede">
            Plan jobs, build quotations, and track project costs from dry-dock
            to delivery, all in one workspace.
          </p>
          <div class="dj-login__meta">
            <span class="dj-login__dot"></span>
            Trusted for project &amp; quotation management
          </div>
        </div>
      </div>
    </section>

    <!-- ================= FORM ================= -->
    <section class="dj-login__panel">
      <div class="dj-login__form">
        <v-img
          :src="require('../../assets/logo.png')"
          max-width="52"
          contain
          class="dj-login__form-logo"
        ></v-img>

        <p class="dj-login__eyebrow">Secure access</p>
        <h2 class="dj-login__title" v-text="loggedInRole + ' Login'"></h2>
        <p class="dj-login__subtitle">
          Enter your credentials to access your workspace.
        </p>

        <v-text-field
          v-model="form.email"
          outlined
          label="Email"
          prepend-inner-icon="mdi-email-outline"
          :error-messages="errors.email"
          class="dj-login__field"
          hide-details="auto"
        ></v-text-field>

        <v-text-field
          v-model="form.password"
          type="password"
          outlined
          label="Password"
          prepend-inner-icon="mdi-lock-outline"
          :error-messages="errors.password"
          class="dj-login__field"
          hide-details="auto"
          @keyup.enter="login"
        ></v-text-field>

        <v-btn
          block
          large
          color="primary"
          class="dj-login__submit"
          :loading="isLoading"
          @click="login"
        >
          Sign in
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>

        <p class="dj-login__footnote">
          Protected workspace · Contact your administrator for access
        </p>
      </div>
    </section>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "LoginPage",
  data() {
    return {
      form: {
        email: "",
        password: "",
      },
      loggedInRole: "",
      isLoading: false,
    };
  },
  mounted() {
    if (this.$route.path == "/superadmin/login")
      this.loggedInRole = "SUPER ADMIN";
    if (this.$route.path == "/admin/login") this.loggedInRole = "SCHOOL ADMIN";
    if (this.$route.path == "/teacher/login") this.loggedInRole = "TEACHER";
    if (this.$route.path == "/student/login") this.loggedInRole = "STUDENT";
    if (this.$route.path == "/infakt_teacher/login")
      this.loggedInRole = "INFAKT TEACHER";
  },
  methods: {
    ...mapActions({
      logIn: "auth/logIn",
    }),
    async login() {
      this.isLoading = true;
      this.logIn(this.form)
        .then(() => {
          location.reload("/pan-dashboard");
          this.isLoading = false;
        })
        .catch(() => {
          this.isLoading = false;
        });
    },
  },
};
</script>

<style scoped>
.dj-login {
  display: grid;
  grid-template-columns: 1.15fr 1fr;
  min-height: 100vh;
  width: 100%;
  background: var(--dj-bg);
}
.dj-login__hero {
  position: relative;
  overflow: hidden;
  display: flex;
  isolation: isolate;
}
.dj-login__video {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -2;
}
.dj-login__scrim {
  position: absolute;
  inset: 0;
  z-index: -1;
  background: linear-gradient(
      205deg,
      oklch(0.22 0.05 255 / 0.55) 0%,
      oklch(0.16 0.05 258 / 0.72) 55%,
      oklch(0.13 0.05 260 / 0.92) 100%
    ),
    radial-gradient(
      120% 90% at 80% 0%,
      oklch(0.6 0.17 254 / 0.28) 0%,
      transparent 60%
    );
}
.dj-login__hero-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 44px 56px;
  width: 100%;
  color: #fff;
}
.dj-login__brand {
  display: flex;
  align-items: center;
  gap: 14px;
  text-decoration: none;
}
.dj-login__logo {
  background: oklch(1 0 0 / 0.94);
  border-radius: 12px;
  padding: 7px;
  box-shadow: 0 6px 18px oklch(0 0 0 / 0.28);
}
.dj-login__brand-name {
  font-size: 1.5rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1;
  color: #fff;
}
.dj-login__brand-sub {
  font-size: 0.72rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: oklch(0.92 0.02 255 / 0.75);
  margin-top: 3px;
}
.dj-login__headline {
  font-size: clamp(2.1rem, 3.4vw, 3.1rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  margin: 0 0 18px;
  text-shadow: 0 2px 20px oklch(0 0 0 / 0.35);
}
.dj-login__lede {
  font-size: 1.02rem;
  line-height: 1.6;
  max-width: 30rem;
  color: oklch(0.94 0.01 255 / 0.86);
  margin: 0 0 26px;
}
.dj-login__meta {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.82rem;
  color: oklch(0.94 0.01 255 / 0.8);
}
.dj-login__dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: oklch(0.72 0.17 254);
  box-shadow: 0 0 0 4px oklch(0.6 0.17 254 / 0.28);
}
.dj-login__panel {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px;
  background: var(--dj-surface);
}
.dj-login__form {
  width: 100%;
  max-width: 380px;
}
.dj-login__form-logo {
  display: none;
  margin-bottom: 20px;
}
.dj-login__eyebrow {
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--dj-accent);
  margin: 0 0 8px;
}
.dj-login__title {
  font-size: 1.9rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: var(--dj-text);
  margin: 0 0 8px;
  line-height: 1.1;
}
.dj-login__subtitle {
  font-size: 0.95rem;
  color: var(--dj-text-soft);
  margin: 0 0 30px;
}
.dj-login__field {
  margin-bottom: 18px;
}
.dj-login__submit {
  margin-top: 6px;
  height: 50px !important;
  font-size: 1rem !important;
  border-radius: var(--dj-radius-sm) !important;
  box-shadow: 0 8px 22px -8px var(--dj-accent-ring) !important;
}
.dj-login__footnote {
  margin-top: 22px;
  font-size: 0.78rem;
  color: var(--dj-text-faint);
  text-align: center;
}
@media (max-width: 960px) {
  .dj-login {
    grid-template-columns: 1fr;
  }
  .dj-login__hero {
    display: none;
  }
  .dj-login__form-logo {
    display: block;
  }
  .dj-login__panel {
    min-height: 100vh;
  }
}
</style>
